import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/Icon';

function EraDivider({ era_name }) {
  return (
    <div className="lor-milestone-era-divider">
      <span className="lor-milestone-era-divider-label">{era_name}</span>
    </div>
  );
}

function MilestoneItem({ milestone }) {
  const navigate = useNavigate();
  const {
    year, date, icon, title, description,
    era, link,
  } = milestone;

  const hasLink = !!link;

  const handleClick = () => {
    if (hasLink) navigate(link);
  };

  return (
    <div
      className={`lor-milestone-item${hasLink ? ' lor-milestone-item--linked' : ''}`}
      onClick={hasLink ? handleClick : undefined}
      role={hasLink ? 'button' : undefined}
      tabIndex={hasLink ? 0 : undefined}
      onKeyDown={hasLink ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
    >
      <div className="lor-milestone-year-col">
        <div className="lor-milestone-year">{year}</div>
        {date && <div className="lor-milestone-date">{date}</div>}
      </div>
      <div className="lor-milestone-icon-wrap">
        <span className="lor-milestone-icon">{icon || '📌'}</span>
      </div>
      <div className="lor-milestone-content">
        <div className="lor-milestone-title">
          {title}
          {hasLink && <span className="lor-milestone-link-arrow"> →</span>}
        </div>
        {description && (
          <div className="lor-milestone-desc">{description}</div>
        )}
        {era && (
          <span className="lor-milestone-era-badge">{era}</span>
        )}
      </div>
    </div>
  );
}

export default function LORMilestones({ api }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch(`${api}/api/lor/lore/milestones`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Milestones unavailable.'); setLoading(false); });
  }, [api]);

  if (loading) return <div className="lor-loading">Loading Milestones…</div>;
  if (error)   return <div className="lor-empty">{error}</div>;

  const milestones = data?.milestones || data || [];
  const subtitle   = data?.subtitle || 'Every defining moment, in order.';

  // Group milestones by era so we can render era dividers
  const grouped = [];
  let lastEra = null;
  for (const m of milestones) {
    if (m.era && m.era !== lastEra) {
      grouped.push({ type: 'divider', era_name: m.era });
      lastEra = m.era;
    }
    grouped.push({ type: 'milestone', data: m });
  }

  return (
    <div className="lor-milestones-page">
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📅" size={18} /> League Milestones</h2>
        <div className="lor-page-meta">{subtitle}</div>
      </div>

      <div className="lor-milestones-feed lor-card">
        {milestones.length === 0 && (
          <div className="lor-empty">No milestone data available yet.</div>
        )}
        {grouped.map((item, i) => {
          if (item.type === 'divider') {
            return <EraDivider key={`divider-${item.era_name}-${i}`} era_name={item.era_name} />;
          }
          return (
            <MilestoneItem key={item.data.title || i} milestone={item.data} />
          );
        })}
      </div>
    </div>
  );
}
